import { Instance, types, cast } from 'mobx-state-tree';
import { oysterPayUrl } from '@configs/variables';
import PaymentStatus, {
  ONLY_CARD_FORM,
  PaymentLinkRedirectStatus,
  PaymentLinkStatus,
  PaymentLinkType,
  TicketStatus,
} from '@constants/paymentLink';

import getUrlParam from '@utils/getUrlParam';
import { PaymentMethod } from '@store/checkout';

export const emptyItem = {
  paymentLinkItemId: '',
  paymentLinkId: '',
  name: '',
  quantity: '',
  price: '',
  total: '0.00',
};

export const defaultItem = {
  paymentLinkItemId: '',
  paymentLinkId: '',
  name: '',
  quantity: 1,
  price: 0,
  total: 0,
};

const LinkItem = types.model({
  paymentLinkItemId: '',
  paymentLinkId: '',
  name: '',
  quantity: types.optional(types.union(types.number, types.string), ''),
  price: types.optional(types.union(types.number, types.string), ''),
  total: types.optional(types.union(types.number, types.string), ''),
});

const Subscription = types.model({
  payerId: types.optional(types.string, ''),
  planName: types.optional(types.string, ''),
  periodicity: types.optional(types.string, ''),
  startDate: types.optional(types.string, ''),
  endDate: types.optional(types.string, ''),
  payerTaxId: types.optional(types.string, ''),
});

const Settings = types.model({
  redirectAuto: types.optional(types.string, ''),
  redirectUrl: types.optional(types.string, ''),
});

const Invoice = types.model({
  name: types.optional(types.string, ''),
  price: types.optional(types.string, ''),
  uuid: types.optional(types.string, ''),
  currencyRate: types.optional(types.string, ''),
  currency: types.optional(types.string, ''),
  issueDate: types.optional(types.string, ''),
  dueDate: types.optional(types.string, ''),
  expensionDate: types.optional(types.string, ''),
  importMO: types.optional(types.string, ''),
});

const Contract = types.model({
  policyId: types.optional(types.string, ''),
  invoices: types.optional(types.array(Invoice), []),
  solution: types.optional(types.string, ''),
  nextChargeDate: types.optional(types.string, ''),
  customerName: types.optional(types.string, ''),
});

const Sura = types.model({
  contracts: types.optional(types.array(Contract), []),
  source: types.optional(types.string, ''),
  affiliationName: types.optional(types.string, ''),
  onlySubscription: types.optional(types.boolean, false),
  subscriptionPublicApiKey: types.optional(types.string, ''),
  subscriptionMerchantId: types.optional(types.string, ''),
  subscriptionPartner: types.optional(types.string, ''),
});

const PayeeInfo = types.model({
  firstName: types.optional(types.string, ''),
  lastName: types.optional(types.string, ''),
  email: types.optional(types.string, ''),
  street: types.optional(types.string, ''),
  city: types.optional(types.string, ''),
  state: types.optional(types.string, ''),
  country: types.optional(types.string, ''),
  phone: types.optional(types.string, ''),
  cp: types.optional(types.string, ''),
});

const PaymentLink = types
  .model({
    paymentLinkId: '',
    userId: '',
    userFirstName: '',
    userMiddleName: '',
    userLastName: '',
    businessId: '',
    linkNumber: '',
    title: '',
    status: '',
    disbursedAmount: types.optional(types.union(types.number, types.string), '0.00'),
    total: types.optional(types.union(types.number, types.string), '0.00'),
    payeeName: '',
    payeeEmail: '',
    createdAt: '',
    type: types.optional(types.string, PaymentLinkType.SINGLE_PAYMENT),
    lastPaidAt: '',
    useLimit: types.optional(types.union(types.number, types.string), 0),
    paidCount: types.optional(types.union(types.number, types.string), 0),
    linkItems: types.optional(types.array(LinkItem), [emptyItem]),
    currency: '',
    paymentMethod: '',
    isSubscription: false,
    subscription: types.optional(Subscription, {}),
    settings: types.optional(Settings, {}),
    sura: types.optional(Sura, {}),
    externalRefId: '',
    exchangeRate: types.optional(types.string, ''),
    isAvailableInterestFreeMonths: false,
    allowDomiciliation: false,
    payeeInfo: types.optional(PayeeInfo, {}),
  })
  .views((self) => ({
    get linkNumberFormatted() {
      const sequence = self.linkNumber.padStart(9, '0');
      return `#${sequence}`;
    },
    get userFullName() {
      const { userFirstName, userMiddleName, userLastName } = self;
      return [userFirstName, userMiddleName, userLastName].filter(Boolean).join(' ');
    },
    get exists() {
      return !!self.paymentLinkId.length && !!self.linkNumber;
    },
    get mappedStatus() {
      if (!this.isUnlimited && +self.paidCount >= +self.useLimit) {
        return TicketStatus.PAID;
      }
      const status = self.status || PaymentLinkStatus.UNKNOWN;
      return PaymentStatus[`${status}`] || TicketStatus.PAID;
    },
    get displayStatus() {
      switch (getUrlParam('status')) {
        case PaymentLinkRedirectStatus.SUCCESS:
          return TicketStatus.PAID;
        default:
          return this.mappedStatus;
      }
    },
    get isPaid() {
      if (!this.isUnlimited && +self.paidCount >= +self.useLimit) {
        return true;
      }
      const status = self.status || PaymentLinkStatus.UNKNOWN;
      return PaymentStatus[`${status}`] === TicketStatus.PAID;
    },
    get isSubscribed() {
      const status = self.status || PaymentLinkStatus.UNKNOWN;
      return PaymentStatus[`${status}`] === TicketStatus.SUBSCRIBED;
    },
    get isMultiple() {
      return self.type === PaymentLinkType.MULTI_PAYMENT;
    },
    get isUnlimited() {
      return +self.useLimit === 0;
    },
    get paymentLinkLink() {
      return `${oysterPayUrl}/${self.paymentLinkId}`;
    },
    get isOnlyCardFormRequired() {
      return ONLY_CARD_FORM.includes(self.paymentMethod as any);
    },
    get isOnlyDirectDebitFormRequired() {
      return self.paymentMethod === PaymentMethod.directDebit;
    },
  }))
  .actions((self) => ({
    updatePaymentLinkStatus: (status: string) => {
      self.status = cast(status);
    },
    updatePaymentLink: (data: Instance<typeof self>) => {
      self.paymentLinkId = data.paymentLinkId;
      self.userId = data.userId;
      self.userFirstName = data.userFirstName;
      self.userMiddleName = data.userMiddleName;
      self.userLastName = data.userLastName;
      self.businessId = data.businessId;
      self.title = data.title;
      self.status = data.status;
      self.linkNumber = data.linkNumber;
      self.disbursedAmount = Number(+data.disbursedAmount / 100).toFixed(2);
      self.total = Number(+data.total / 100).toFixed(2);
      self.payeeName = data.payeeName;
      self.payeeEmail = data.payeeEmail;
      self.createdAt = data.createdAt;
      self.type = data.type;
      self.useLimit = data.useLimit;
      self.paidCount = data.paidCount;
      self.lastPaidAt = data.lastPaidAt;
      self.linkItems = cast(
        data.linkItems.map((item: LinkItemStore) => ({
          ...defaultItem,
          ...item,
          quantity: item.quantity ? +item.quantity : 0,
          price: item.price ? Number(+item.price / 100).toFixed(2) : 0,
          total: item.total ? Number(+item.total / 100).toFixed(2) : 0,
        })),
      );
      self.currency = data.currency;
      self.paymentMethod = data.paymentMethod;
      self.isSubscription = data.isSubscription;
      self.subscription = cast(data.subscription);
      self.settings = cast(data.settings);
      self.sura = cast(data.sura);
      self.externalRefId = data.externalRefId;
      self.exchangeRate = data.exchangeRate;
      self.isAvailableInterestFreeMonths = data.isAvailableInterestFreeMonths;
      self.allowDomiciliation = data.allowDomiciliation;
      self.payeeInfo = cast(data.payeeInfo);
    },
    resetPaymentLink: () => {
      self.title = '';
      self.paymentLinkId = '';
      self.userId = '';
      self.userFirstName = '';
      self.userMiddleName = '';
      self.userLastName = '';
      self.businessId = '';
      self.linkNumber = '';
      self.title = '';
      self.status = '';
      self.disbursedAmount = 0;
      self.total = 0;
      self.payeeName = '';
      self.payeeEmail = '';
      self.createdAt = '';
      self.type = PaymentLinkType.SINGLE_PAYMENT;
      self.useLimit = 1;
      self.paidCount = 0;
      self.lastPaidAt = '';
      self.linkItems.replace([emptyItem]);
      self.paymentMethod = '';
      self.isSubscription = false;
      self.subscription = cast({});
      self.settings = cast({});
      self.sura = cast({});
      self.externalRefId = '';
      self.exchangeRate = '';
      self.isAvailableInterestFreeMonths = false;
      self.allowDomiciliation = false;
      self.payeeInfo = cast({});
    },
  }));

export interface LinkItemStore extends Instance<typeof LinkItem> {}
export interface PaymentLinkStore extends Instance<typeof PaymentLink> {}
export default PaymentLink;
